import { ChevronRightIcon, DotFilledIcon } from '@radix-ui/react-icons';
import { useEffect, useId, useState } from 'react';
import type { QuestionBankCategory, QuestionBankSection } from '@wx-weiqi/contracts';

import './question-bank-directory.css';

interface DirectorySection {
  readonly section: QuestionBankSection;
  readonly label: string;
  readonly categories: readonly QuestionBankCategory[];
}

interface Props {
  readonly sections: readonly DirectorySection[];
  readonly selectedPointId: string | null;
  readonly loading: boolean;
  readonly onSelect: (pointId: string) => void;
}

/** Presentation only: selections still load problems through QuestionBankStore. */
export function QuestionBankDirectory({ sections, selectedPointId, loading, onSelect }: Props) {
  const id = useId();
  const selectedSection = sections.find((section) =>
    section.categories.some((category) =>
      category.knowledgePoints.some((point) => point.knowledgePointId === selectedPointId),
    ),
  );
  const selectedCategory = selectedSection?.categories.find((category) =>
    category.knowledgePoints.some((point) => point.knowledgePointId === selectedPointId),
  );
  const selectedPoint = selectedCategory?.knowledgePoints.find(
    (point) => point.knowledgePointId === selectedPointId,
  );
  const selection = selectedPoint
    ? [selectedSection?.label, selectedCategory?.name, selectedPoint.name].join(' / ')
    : undefined;
  const [expandedSection, setExpandedSection] = useState<QuestionBankSection | null>(
    selectedSection?.section ?? null,
  );
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(
    selectedCategory?.categoryId ?? null,
  );

  // Reveal the selected path after catalog loading or an external selection change.
  // Manual disclosure changes do not change the selected problems.
  useEffect(() => {
    setExpandedSection(selectedSection?.section ?? null);
    setExpandedCategoryId(selectedCategory?.categoryId ?? null);
  }, [selectedPointId, selectedSection?.section, selectedCategory?.categoryId]);

  return (
    <aside className="question-library-tree question-bank-directory" aria-label="知识分类目录">
      <header className="question-bank-directory__header">
        <h3>题库目录</h3>
        <p>板块 / 分类 / 知识点</p>
      </header>
      <div className="question-bank-directory__scroll" aria-busy={loading}>
        <ul className="question-bank-directory__sections">
          {sections.map(({ section, label, categories }) => {
            const expanded = expandedSection === section;
            const bodyId = `${id}-${section}`;
            return (
              <li className="question-bank-directory__section" data-section={section} key={section}>
                <button
                  className="question-bank-directory__section-button"
                  type="button"
                  aria-label={`${label}${expanded ? '收起' : '展开'}`}
                  aria-expanded={expanded}
                  aria-controls={bodyId}
                  onClick={() => {
                    setExpandedSection(expanded ? null : section);
                    setExpandedCategoryId(
                      !expanded && selectedSection?.section === section
                        ? (selectedCategory?.categoryId ?? null)
                        : null,
                    );
                  }}
                >
                  <DotFilledIcon className="question-bank-directory__dot" aria-hidden="true" />
                  <strong>{label}</strong>
                  <ChevronRightIcon
                    className="question-bank-directory__chevron"
                    aria-hidden="true"
                  />
                </button>
                <ul id={bodyId} className="question-bank-directory__branch" hidden={!expanded}>
                  {categories.map((category) => {
                    const categoryExpanded = expanded && expandedCategoryId === category.categoryId;
                    const pointsId = `${id}-${category.categoryId}`;
                    return (
                      <li className="question-bank-directory__category" key={category.categoryId}>
                        <button
                          className="question-bank-directory__category-button"
                          type="button"
                          aria-label={`${category.name}${categoryExpanded ? '收起' : '展开'}`}
                          aria-expanded={categoryExpanded}
                          aria-controls={pointsId}
                          onClick={() =>
                            setExpandedCategoryId(categoryExpanded ? null : category.categoryId)
                          }
                        >
                          <DotFilledIcon
                            className="question-bank-directory__dot"
                            aria-hidden="true"
                          />
                          <span>{category.name}</span>
                          <ChevronRightIcon
                            className="question-bank-directory__chevron"
                            aria-hidden="true"
                          />
                        </button>
                        <ul
                          id={pointsId}
                          className="question-bank-directory__branch"
                          hidden={!categoryExpanded}
                        >
                          {category.knowledgePoints.map((point) => (
                            <li
                              className="question-bank-directory__leaf"
                              key={point.knowledgePointId}
                            >
                              <button
                                className="question-bank-directory__point"
                                type="button"
                                aria-current={
                                  selectedPointId === point.knowledgePointId ? 'true' : undefined
                                }
                                onClick={() => onSelect(point.knowledgePointId)}
                              >
                                <DotFilledIcon
                                  className="question-bank-directory__dot"
                                  aria-hidden="true"
                                />
                                <span>{point.name}</span>
                                <small>{point.problemCount} 题</small>
                              </button>
                            </li>
                          ))}
                          {category.knowledgePoints.length === 0 ? (
                            <li className="question-bank-directory__empty">暂无知识点</li>
                          ) : null}
                        </ul>
                      </li>
                    );
                  })}
                  {categories.length === 0 ? (
                    <li className="question-bank-directory__empty">
                      {loading ? '正在加载目录…' : '暂无分类'}
                    </li>
                  ) : null}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>
      <footer className="question-bank-directory__selection" aria-live="polite">
        <span>已选：</span>
        <span>{selection ?? '请选择知识点'}</span>
      </footer>
    </aside>
  );
}
